import get from "lodash/get";
import isObject from "lodash/isObject";
import type { IconBoxColors } from "@hightouch/htui";
import type { Edge as ReactFlowEdge, Node as ReactFlowNode } from "reactflow";
import type { ExperimentStatus } from "@hightouch/lib/experiments/types";
import type { ExtendedTypes } from "@hightouch/formkit";
import type { ColumnReference } from "@hightouch/lib/query/visual/types";

import type { Schedule } from "src/components/schedule/types";
import type { SyncWizardOutput } from "src/pages/syncs/create/types";
import {
  type MessageNodeConfig,
  type ContextConfig,
  type CouponAssignmentConfig,
  type DecisionConfig,
  type DecisionOutcome,
  type JourneyContextColumn,
  type EntryCohortConfig,
  type EntryEventConfig,
  type EntryAudienceExperimentConfig,
  type JourneyContextVariableDefinition,
  type JourneyContextVariableType,
  type JourneyExecutionMode,
  type JourneyExitCriteriaConfig,
  type JourneyNodeConfig,
  JourneyNodeSyncMode,
  JourneyStatus,
  type JourneySyncExitConfig,
  type TimeDelayConfig,
  type WaitUntilEventConfig,
  JourneyNodeType,
  NOTE_NODE_TYPE,
} from "src/types/journeys";
import type { NullableProperties } from "src/types/utils";
import type { JourneyEventType } from "./navigation/types";
import type {
  AnalyticsExperimentConfigurationInputFragment,
  CepDynamicContentUsageFragment,
  MessageConfigInput,
} from "src/graphql";
import type { RenderedOutput } from "src/cep/messages/types";

/**
 * MessageConfigInput with `rendered_output` narrowed from jsonb (`any`) to
 * the discriminated union used by the CEP message system.
 */
export type JourneyMessageConfig = Omit<
  MessageConfigInput,
  "rendered_output"
> & {
  rendered_output?: RenderedOutput | undefined;
  message_push_apps?: Array<{
    push_app: {
      id: string;
      name: string;
      platform: string;
      enabled: boolean;
      config: { app_icon_url?: string } | null;
    };
  }>;
  startedExperimentElements?: string[];
  /** Each test's promoted winner by element: once promoted, only that arm sends. Read-only, never saved. */
  promotedVariantKeys?: Record<string, string>;
  messagePersisted?: boolean;
  content_dynamic_slot_usages?: readonly CepDynamicContentUsageFragment[];
};

type BaseSyncConfigDetails = {
  // Flag to denote that this node is a clone of another sync node.
  is_clone?: boolean | null;
};

export type FullSyncConfig = SyncWizardOutput;

/** Sync configs with no destination instance. These are used to create new syncs when the journey is saved. */
export type EphemeralSyncConfigDetails = BaseSyncConfigDetails & {
  // This sync **does not** exist. It's only used to create a new sync when the journey is saved.
  destination_instance_id: null;
  mode: JourneyNodeSyncMode;
  // IMPORTANT: the backend does not allow `null`, but `undefined` does not work with the radio buttons.
  // The exit config must be transformed when receiving and sending the data to the backend.
  exit_config: NullableProperties<JourneySyncExitConfig>;
  // If the sync was created from a sync config with a placeholder destination
  // this will be present so that the associated row in journey_syncs can be updated.
  placeholder_destination_id?: number | null;

  // The sync config that will be used to create the sync
  full_sync_config: FullSyncConfig;
};

/** Sync configs with a destination instance. These are existing syncs that may be updated. */
export type ConfiguredSyncConfigDetails = Omit<
  EphemeralSyncConfigDetails,
  "destination_instance_id" | "full_sync_config"
> & {
  destination_instance_id: number;

  // The sync config that will be used to update the existing sync
  full_sync_config: FullSyncConfig | null;
};

/** Sync configs with a placeholder destination. These are only used for placeholder syncs. */
export type PlaceholderSyncConfigDetails = BaseSyncConfigDetails & {
  placeholder_destination_id: number;
  destination_instance_id: null;
  mode: "placeholder-mode"; // The placeholder value defined in the backend
  exit_config: { [key: string]: never }; // Always an empty object
};

export type SyncConfigDetails =
  | PlaceholderSyncConfigDetails
  | ConfiguredSyncConfigDetails
  | EphemeralSyncConfigDetails;

export type RealtimeSyncConfigDetails = {
  destination_id: string;
  // Match GraphQL JSONObject (`any`) so DeepPartial form values stay assignable.
  subscription_config: Record<string, any>;
};

// One property of the request a Function receives, in the shape the sync mapper saves. Traits come from the
// parent model and journey values from the step that fired.
export type FunctionPayloadMapping =
  | { type: "standard"; to: string; from: ColumnReference | string }
  | { type: "variable"; to: string; variable: string }
  | {
      type: "static";
      to: string;
      value: string | number | boolean | null;
      valueType?: ExtendedTypes;
    };

// The realtime worker resolves Function and Message tile mappings with one resolver.
export type RealtimeTileMapping = FunctionPayloadMapping;

export type RealtimeMessageNodeConfig = MessageNodeConfig & {
  mappings?: RealtimeTileMapping[];
};

// The response model sits beside the request mapping, which follows the sync node's syncConfig shape.
export type FunctionSubscriptionConfig = {
  eventModelId?: string | number;
  syncConfig?: { mappings?: FunctionPayloadMapping[] };
};

export type RealtimeFunctionConfigDetails = {
  function_id: string;
  subscription_config: FunctionSubscriptionConfig;
};

export type JourneyNodeDetails<C extends JourneyNodeConfig> = {
  id: string;
  name: string;
  description: string | null;
  number_users: string | null;
  number_unique_users: string | null;
  // Audience ID for EntryCohort
  // Event ID for WaitUntilEvent and EntryEvent
  segment_id: string | null;
  // Relationship ID for WaitUntilEvent and EntryEvent
  event_relationship_id: string | null;
  // Position of the node in the graph
  // Optional until the frontend changes come in.
  position?: { x: number; y: number } | null;

  // Experiment configuration for Split nodes
  experiment?:
    | (AnalyticsExperimentConfigurationInputFragment & {
        status?: ExperimentStatus;
      })
    | null;

  // Journey sync config for sync nodes
  sync_configs: SyncConfigDetails[] | null;

  realtime_sync_config?: RealtimeSyncConfigDetails | null;

  realtime_function_config?: RealtimeFunctionConfigDetails | null;

  // Journey message config for message nodes.
  // Contains the flat message configuration matching the GraphQL MessageConfigInput.
  // When loaded from backend, has { message_id }. When edited locally via wizard, it includes all fields.
  message_config: JourneyMessageConfig | null;

  // What a decision node optimizes for. Stored on decision_engine_outcomes and
  // read back through the node's flow link, never persisted in node config.
  decision_outcome?: DecisionOutcome | null;

  // Parent model columns a decision node reads as context, as stored on its
  // feature matrix.
  stored_decision_context_columns?: JourneyContextColumn[];

  // Set only by an edit. The save omits it otherwise, so drifted model columns
  // do not block unrelated saves.
  decision_context_columns?: JourneyContextColumn[];

  config: C;
};

type JourneyNoteDetailsBase = {
  id: string;
  content: string;
  created_by: string | null;
  updated_by: string | null;
  created_at: string | null;
  updated_at: string | null;
};

export type JourneyCanvasNote = JourneyNoteDetailsBase & {
  position: { x: number; y: number };
  node_id: null;
};

export type JourneyNoteAttachedToNode = JourneyNoteDetailsBase & {
  node_id: string;
  position: null;
};
export type JourneyNoteDetails = JourneyCanvasNote | JourneyNoteAttachedToNode;

export type JourneyNodePerformanceDetails<C extends JourneyNodeConfig> = {
  isSingleEntity?: boolean;
  branchData?: JourneyNodeDetails<C>;
  duration?: string;
};

export type ExecutableJourneyNodeType = Exclude<
  JourneyNodeType,
  typeof JourneyNodeType.Note
>;

// This is the node type that's used with react flow.
export type JourneyNoteNode = ReactFlowNode<
  Omit<JourneyCanvasNote, "position">
> & {
  type: typeof NOTE_NODE_TYPE;
};
export type JourneyNodeData =
  | JourneyNodeDetails<JourneyNodeConfig>
  | Omit<JourneyCanvasNote, "position">;
export type ExecutableJourneyNode<
  T extends JourneyNodeConfig = JourneyNodeConfig,
> = ReactFlowNode<JourneyNodeDetails<T>>;
export type JourneyNode = ReactFlowNode<JourneyNodeData>;

/**
 * Type guards for journey node types
 */

/**
 * Type guard to check if a node is an executable journey node
 * @param node The journey node to check
 * @returns True if the node is an executable journey node
 */
export function isExecutableJourneyNode(
  node: JourneyNode | JourneyNoteNode | undefined | null,
): node is ExecutableJourneyNode {
  return Boolean(node && node.type !== NOTE_NODE_TYPE);
}

/**
 * Type guard to check if a node is a Note node
 * @param node The journey node to check
 * @returns True if the node is a Note node
 */
export function isJourneyNoteNode(node: JourneyNode): node is JourneyNoteNode {
  return node.type === NOTE_NODE_TYPE;
}

/**
 * Type guard to check if a note is a canvas note
 * @param note The journey note to check
 * @returns True if the note is a canvas note
 */
export function isCanvasNote(
  note: JourneyNoteDetails,
): note is JourneyCanvasNote {
  return note.node_id === null && note.position !== null;
}

export function isEntryCohortNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<EntryCohortConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.EntryCohort
  );
}

export function getEntryAudienceId(
  entryNode: ExecutableJourneyNode | undefined,
): string | null {
  return entryNode && isEntryCohortNode(entryNode)
    ? entryNode.data.segment_id
    : null;
}

export function isEntryEventNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<EntryEventConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.EntryEvent
  );
}

export function isEntryAudienceExperimentNode(
  node: JourneyNode,
): node is JourneyNode & {
  data: JourneyNodeDetails<EntryAudienceExperimentConfig>;
} {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.EntryAudienceExperiment
  );
}

/**
 * Type guard to check if a node is a TimeDelay node
 * @param node The journey node to check
 * @returns True if the node is a TimeDelay node
 */
export function isTimeDelayNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<TimeDelayConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.TimeDelay
  );
}

/**
 * Type guard to check if a node is a WaitUntilEvent node
 * @param node The journey node to check
 * @returns True if the node is a WaitUntilEvent node
 */
export function isWaitUntilEventNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<WaitUntilEventConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.WaitUntilEvent
  );
}

/**
 * Type guard to check if a node is a Context node
 * @param node The journey node to check
 * @returns True if the node is a Context node
 */
export function isContextNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<ContextConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.Context
  );
}

export function isCouponAssignmentNode(
  node: JourneyNode,
): node is JourneyNode & {
  data: JourneyNodeDetails<CouponAssignmentConfig>;
} {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.CouponAssignment
  );
}

export function isDecisionNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<DecisionConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.Decision
  );
}

export function isMessageNode(
  node: JourneyNode,
): node is JourneyNode & { data: JourneyNodeDetails<MessageNodeConfig> } {
  return (
    "config" in node.data &&
    node?.data?.config?.type === JourneyNodeType.Message
  );
}

export type JourneyContextConfig = {
  variables: JourneyContextVariableDefinition[];
};

export type JourneyGraph = {
  journey: {
    id: string;
    name: string;
    description: string | null;
    status: JourneyStatus | JourneyTemplateState;
    schedule: Schedule | null;
    exitCriteria: JourneyExitCriteriaConfig;
    contextConfig: JourneyContextConfig;
  };
  nodes: ExecutableJourneyNode[];
  edges: ReactFlowEdge[];
  notes: JourneyNoteDetails[];
};

export type StagedJourneyGraph = JourneyGraph & {
  journey: {
    version_id?: string;
    status: JourneyStatus.Staged;
  };
};

export type NodeColor = IconBoxColors;

export type { JourneyState } from "@hightouch/lib/cep/types/journey-state";

export type JourneyTemplateState = "template";

export type NodeDetailFormProps<C extends JourneyNodeConfig> = {
  id: string;
  data: JourneyNodeDetails<C>;
  onClose: () => void;
};

export type CreateJourneyForm = {
  name: string;
  description: string | null;
  parentModelId: number | null;
  templateId: string | null;
  contextConfig: JourneyContextConfig | null;
  executionMode: JourneyExecutionMode;
  labelIds: string[];
};

export type CreateJourneyTemplateForm = {
  name: string;
  description: string;
  parentModelId: number;
};

export type CloneJourneyInput = {
  sourceJourneyId: string;
  name: string;
  description: string | null;
  parentModelId: number;
  contextConfig: JourneyContextConfig | null;
};

export type SegmentPriorityList = {
  id: string;
  name: string;
}[];

export type SplitBranch = {
  id: string;
  name: string;
  percentage: number;
};

export type JourneyRun = {
  id: string;
  status: string;
  error: string | null;
  started_at: string | null;
  finished_at: string | null;
  entry_count: string;
  unique_entry_count: string;
  exit_count: string;
  unique_exit_count: string;
  completed_count: string;
  unique_completed_count: string;
  journey_node_runs: JourneyNodeRun[];
};

export type JourneyNodeRun = {
  node_id: string;
  status: string;
  error: string | null;
  run_id?: string;
  entry_count: string;
  unique_entry_count: string;
  advanced_count: string;
  unique_advanced_count: string;
  exit_count: string;
  unique_exit_count: string;
  completed_count: string;
  unique_completed_count: string;
};

export function isPlaceholderSyncConfig(
  config: any,
): config is PlaceholderSyncConfigDetails {
  return (
    isObject(config) &&
    get(config, "mode") === "placeholder-mode" &&
    get(config, "placeholder_destination_id") != null
  );
}

export function isConfiguredSyncConfig(
  config: any,
): config is ConfiguredSyncConfigDetails {
  return (
    isObject(config) &&
    Object.values(JourneyNodeSyncMode).includes(
      get(config, "mode") as JourneyNodeSyncMode,
    ) &&
    get(config, "destination_instance_id") != null
  );
}

export function isEphemeralSyncConfig(
  config: any,
): config is EphemeralSyncConfigDetails {
  return (
    isObject(config) &&
    Object.values(JourneyNodeSyncMode).includes(
      get(config, "mode") as JourneyNodeSyncMode,
    ) &&
    get(config, "destination_instance_id") === null
  );
}

export function isStagedJourney(
  graph: JourneyGraph | StagedJourneyGraph | null | undefined,
): graph is StagedJourneyGraph {
  if (!graph?.journey || graph.journey.status !== JourneyStatus.Staged) {
    return false;
  }
  const versionId = (graph as StagedJourneyGraph).journey.version_id;
  return Boolean(versionId && versionId.trim() !== "");
}

export type JourneyLogRow = {
  event_row_id: string | null;
  event_model_id: string | null;
  to_node_id: string | null;
  from_node_id: string | null;
  event_type: JourneyEventType;
  timestamp: string;
  run_id: string;
  row_instance_id: string;
  row_id: string;
};

export type JourneySimulationConfig = {
  id: string;
  journey_version_id: string;
  entry_execution_timestamp: string;
  config: Record<string, string>;
};

export type CreateJourneyArgs = {
  name: string;
  description: string | null;
  parentModelId: number;
  schedule: Schedule | null;
};

export const ContextDataTypesValues: JourneyContextVariableType[] = [
  "STRING",
  "NUMBER",
  "BOOLEAN",
];

export type EmptyContextVariable = {
  variable_name: "";
  variable_type: "";
  value: null;
};

export type { JourneyExecutionMode };
